import * as Yup from "yup";
import moment from 'moment';

export const DetailsValidationSchema = Yup.object().shape({
  name: Yup.string().min(2, "Trenger minimum 2").required("Mangler navn"),
  location: Yup.object().shape({
    streetName: Yup.string().nullable().min(2, "Trenger minimum 2"),
    houseNumber: Yup.string().nullable().when(['streetName'],{
      is: (streetName: string) => streetName !== undefined && streetName !== null && streetName.length > 1,
      then: Yup.string().nullable().matches(/^[0-9\b]+$/, "Kun tall er gyldig").required("Mangler"),
      otherwise: Yup.string().nullable().test(
        'is-empty-or-error',
        'Feltet må være tomt hvis gate er tomt',
        value => value === null || value === '' || value === undefined
      )
    }
    ),
    houseLetter: Yup.string().nullable().when(['streetName'],{
      is: (streetName: string) => streetName !== undefined && streetName !== null && streetName.length > 1,
      then: Yup.string().nullable().matches(/^[a-zA-ZæøåÆØÅ]*$/, "Kun bokstaver er gyldig"),
      otherwise: Yup.string().nullable().test(
        'is-empty-or-error',
        'Feltet må være tomt hvis gate er tomt',
        value => value === null || value === '' || value === undefined
      )
    }),
    postalCode: Yup.string().matches(/^\d{4}$/, "Postnummer er ikke gyldig").required("Mangler")
  }, [['id', 'streetName']]),
  postBox: Yup.object().shape({
    number: Yup.string().when(['postalCode'], {
      is: (postalCode:string) => postalCode !== undefined,
      then: Yup.string().matches(/^[0-9\b]+$/, "Kun tall er gyldig").required("Mangler")
    }),
    postOffice: Yup.string().nullable().matches(/^(?![\s.-])[a-zA-ZæøåÆØÅ.\-\s]*(?<![\s.-])$/, "Kun bokstaver, punktum, bindestrek og mellomrom er tillatt. Feltet kan ikke starte eller slutte med disse tegnene."),
    postalCode: Yup.string().when(['number'], {
      is: (number:string) => number !== undefined,
      then: Yup.string().matches(/^\d{4}$/, "Postnummer er ikke gyldig").required("Mangler")
    })
  },[
  ['postalCode', 'number'],
]).nullable()
});

const removePrefix = (value: string): string => {
  if (value.startsWith('0047')) {
    return value.slice(4);
  } else if (value.startsWith('+47')) {
    return value.slice(3);
  } else if (value.startsWith('47') && value.length > 8) {
    return value.slice(2);
  }
  return value;
};


export const PhoneNumbersValidationSchema = Yup.object().shape({
  phoneNumbers: Yup.array().of(Yup.object().shape({
    // value: Yup.string().matches(/^(0047|\+47)?\d{5,6}|\d{8}$/, 'Telefonnummer er ikke gyldig'),
    value: Yup.string()
    .when(['source','deleted'], {
      is: (source: string, deleted: boolean) => !deleted && (source === undefined || source === null || source === ""),
      then: Yup.string()
        .transform((value: string) => removePrefix(value))
        .matches(/^(?:[2-9]\d{7})$/, 'Telefonnummer er ikke gyldig')
    }),
    label: Yup.string().test(
      'missing_value',
      'Telefonnummer kan ikke være blank',
      function(v) {
        const { value, deleted  } = this.parent;

        if(v === undefined || v === null || deleted ) {
          return true;
        }
        return !!value;
      }
    ).nullable()
  },[['value', 'source'],['source','deleted'],['value', 'deleted']]))
});

export const isValidPhoneNumber = (phoneNumber:string) => {
  try {
    PhoneNumbersValidationSchema.validateSync({
      phoneNumbers: [{ value: phoneNumber }]
    });
    return true;
  } catch (error) {
    return false;
  }
};

export const LinksValidationSchema = Yup.object().shape({
  emails: Yup.array().of(Yup.object().shape({
    value: Yup.string()
    .nullable()
    .notRequired()
    .when('value', {
      is: (value:string) => value?.length,
      then: (rule) => rule.email('Skriv inn gyldig epost').matches(/^[\w\-.åæøÅÆØ]+@([\w\-åæøÅÆØ]+\.)+[\w\-åæøÅÆØ]{2,}$/gm, {message: 'Skriv inn gyldig epost', excludeEmptyString: true})
    })
  },[['value', 'value']])),
  urls: Yup.array().of(Yup.object().shape({
    value: Yup.string()
    .nullable()
    .notRequired()
    .when('value', {
      is: (value:string) => value?.length,
      then: (rule) => rule.matches(
        /^(?:(?:https?):\/\/)?(?:www\.)?[a-z0-9-æøåÆØÅ]+(?:\.[a-z0-9-æøåÆØÅ]+)+[^\s]*$/i,
        'Skriv inn gyldig url'
    )
    })
  },[['value', 'value']])),
  socialMediaUrls: Yup.array().of(Yup.object().shape({
    value: Yup.string()
    .nullable()
    .notRequired()
    .when('value', {
      is: (value:string) => value?.length,
      then: (rule) => rule.matches(
        /^(?:(?:https?):\/\/)?(?:www\.)?[a-z0-9-æøåÆØÅ]+(?:\.[a-z0-9-æøåÆØÅ]+)+[^\s]*$/i,
        'Skriv inn gyldig url'
    )
    })
  },[['value', 'value']]))
});



export const IndustriesValidationSchema = Yup.object().shape({

});

const isSameOrBefore = (startTime:string | undefined, endTime:string | undefined) => {
  if(moment(endTime, 'HH:mm').isBetween(moment("00:00", "HH:mm"),moment("07:01", "HH:mm"))) {
    return true;
  }
  return moment(startTime, 'HH:mm').isSameOrBefore(moment(endTime, 'HH:mm'));
}

export const OpeningHoursValidationSchema = Yup.object().shape({
  openingHours: Yup.object().shape({
    showOpeningHours: Yup.boolean().required(''),
    days: Yup.array().of(Yup.object().shape({
      name: Yup.string(),
      opens: Yup.string().test(
        'not_empty',
        'Åpningstid kan ikke være blank',
        function(value) {
          return !!Object.keys(this.parent).length ? !!value : true;
        }
      )
      .test(
        "opens_test",
        "Åpningstid må være tidligere enn stengetid",
        function(value) {
          const { closes } = this.parent;
          return !!Object.keys(this.parent).length ? isSameOrBefore(value, closes) : true;
        }
      ),
      closes: Yup.string()
    }))
  })
});

export const ValidationSchema = Yup.object().shape({
  
});